import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { SuccessResponse } from 'src/apis/dto/common';
import {
  AgeVerificationStatus,
  DTOAgeVerificationStatusResponse,
} from 'src/apis/dto/DTOAgeVerificationStatus';
import { DTOLivenessClientKeysResponse } from 'src/apis/dto/DTOClientKeysResponse';
import { DTOFaceAgeVerificationRequest } from 'src/apis/dto/DTOFaceAgeVerificationRequest';
import { DTOLivenessCheckRequest } from 'src/apis/dto/DTOLivenessCheckRequest';
import { DTOLivenessCheckResponse } from 'src/apis/dto/DTOLivenessCheckResponse';
import { DTOLivenessCheckResultResponse } from 'src/apis/dto/DTOLivenessCheckResultResponse';
import { DTOLivenessGuideResponse } from 'src/apis/dto/DTOLivenessGuideResponse';
import { DTOLivenessSessionTokenResponse } from 'src/apis/dto/DTOLivenessSessionTokenResponse';
import { DTOYotiAvsHeadlessGuideResponse } from 'src/apis/dto/DTOYotiAvsHeadlessGuideResponse';
import { DTOYotiAvsHeadlessSessionRequest } from 'src/apis/dto/DTOYotiAvsHeadlessSessionRequest';
import { DTOYotiAvsHeadlessSessionResponse } from 'src/apis/dto/DTOYotiAvsHeadlessSessionResponse';
import { DTOYotiClientKeyResponse } from 'src/apis/dto/DTOYotiClientKeyResponse';
import { DTOYotiIdvResultResponse } from 'src/apis/dto/DTOYotiIdvResultResponse';
import { DTOYotiLivenessCheckRequest } from 'src/apis/dto/DTOYotiLivenessCheckRequest';
import { DTOYotiLivenessCheckResponse } from 'src/apis/dto/DTOYotiLivenessCheckResponse';
import { DTOYotiLivenessCheckResultResponse } from 'src/apis/dto/DTOYotiLivenessCheckResultResponse';
import { DTOYotiLivenessCheckV2Request } from 'src/apis/dto/DTOYotiLivenessCheckV2Request';
import { DTOYotiLivenessGuideResponse } from 'src/apis/dto/DTOYotiLivenessGuideResponse';
import useQueryWithAuth from 'src/hooks/useQueryWithAuth';
import { RestMutationOptions, useRestMutation } from 'src/hooks/useRestMutation';
import { RestQueryOptions, useRestQuery } from 'src/hooks/useRestQuery';
import { client, ErrorResponse } from 'src/utils/api';
import { optimisticUpdaterFactory } from 'src/utils/query';

enum AgeVerificationApiList {
  GetAgeVerificationStatus = '/api/v1/age-verification/status',
  RequestFaceAgeVerification = '/api/v1/age-verification/face',
  FaceTecClientKeys = '/tsp/v1/age-verification/liveness/client-keys',
  FaceTecSessionToken = '/tsp/v1/age-verification/liveness/session-token',
  FaceTecLivenessCheck = '/api/v1/age-verification/liveness',
  FaceTecLivenessGuide = '/api/v1/age-verification/liveness/guide',
  FaceTecLivenessCheckResult = '/api/v1/age-verification/liveness/results',
  YotiClientKey = '/api/v1/age-verification/liveness/yoti/client-sdk-key',
  YotiLivenessCheck = '/api/v1/age-verification/liveness/yoti',
  YotiLivenessCheckV2 = '/api/v1/age-verification/liveness/yoti/v2',
  YotiLivenessGuide = '/api/v1/age-verification/liveness/yoti/guide',
  YotiLivenessCheckResult = '/api/v1/age-verification/liveness/yoti/results',
  YotiAvsHeadlessGuide = '/api/v1/age-verification/liveness/yoti/avs/headless/guide',
  YotiAvsHeadlessSession = '/api/v1/age-verification/liveness/yoti/avs/headless/session',
}

export const AGE_VERIFICATION_QUERY_KEYS = {
  getAgeVerificationStatus: () => [AgeVerificationApiList.GetAgeVerificationStatus],
};

export const useAgeVerificationStatusQuery = () => {
  return useQueryWithAuth<void, DTOAgeVerificationStatusResponse, void>({
    path: AgeVerificationApiList.GetAgeVerificationStatus,
    queryKey: AGE_VERIFICATION_QUERY_KEYS.getAgeVerificationStatus(),
  });
};

export const useRequestFaceAgeVerificationMutation = (
  options: RestMutationOptions<DTOFaceAgeVerificationRequest> = {}
) => {
  return useRestMutation<DTOFaceAgeVerificationRequest>({
    method: 'post',
    path: AgeVerificationApiList.RequestFaceAgeVerification,
    options,
  });
};

export const useFaceTecClientKeysQuery = (
  options: RestQueryOptions<DTOLivenessClientKeysResponse, void> = {}
) => {
  return useRestQuery<void, DTOLivenessClientKeysResponse, void>({
    path: AgeVerificationApiList.FaceTecClientKeys,
    options,
  });
};

export const useFaceTecSessionTokenQuery = (
  options: RestQueryOptions<DTOLivenessSessionTokenResponse, void> = {}
) => {
  return useRestQuery<void, DTOLivenessSessionTokenResponse, void>({
    path: AgeVerificationApiList.FaceTecSessionToken,
    options,
  });
};

export const useFaceTecLivenessCheckMutation = (
  options: RestMutationOptions<DTOLivenessCheckRequest, DTOLivenessCheckResponse> = {}
) => {
  return useRestMutation<DTOLivenessCheckRequest, DTOLivenessCheckResponse>({
    path: AgeVerificationApiList.FaceTecLivenessCheck,
    method: 'post',
    options,
  });
};

export const useFaceTecLivenessGuideQuery = () => {
  return useRestQuery<void, DTOLivenessGuideResponse, void>({
    path: AgeVerificationApiList.FaceTecLivenessGuide,
  });
};

export const useChangeAgeVerificationStatus = () => {
  const queryClient = useQueryClient();

  const changeAgeVerificationStatus = (status: AgeVerificationStatus) => {
    queryClient.setQueryData<AxiosResponse<DTOAgeVerificationStatusResponse>>(
      AGE_VERIFICATION_QUERY_KEYS.getAgeVerificationStatus(),
      optimisticUpdaterFactory<AxiosResponse<DTOAgeVerificationStatusResponse>>((prev) => {
        prev.data.result.status = status;
      })
    );
  };

  return changeAgeVerificationStatus;
};

const POLLING_INTERVAL_MS = 2500;
/** CREATED 상태 폴링 상한. 2.5s × 48 ≈ 2분. 미제출/스턱 세션이 verifying에 고착되지 않도록. */
export const MAX_IDV_CREATED_POLLING_ATTEMPTS = 48;

export const useFaceTecLivenessCheckResultPolling = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearPolling = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  useEffect(() => clearPolling, []);

  const poll = useCallback((requestId: string) => {
    clearPolling();

    return new Promise<DTOLivenessCheckResultResponse>((resolve, reject) => {
      const executePolling = async () => {
        try {
          const { data } = await client.get<SuccessResponse<DTOLivenessCheckResultResponse>>(
            AgeVerificationApiList.FaceTecLivenessCheckResult,
            { params: { requestId } }
          );

          switch (data.result.processingStatus) {
            case 'PENDING':
              timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
              break;
            case 'COMPLETED':
              resolve(data.result);
              break;
            case 'FAILED':
              reject(new Error('Liveness check failed'));
          }
        } catch (error) {
          reject(error);
        }
      };

      timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
    });
  }, []);

  return { poll };
};

export const useYotiClientKeyQuery = () => {
  return useRestQuery<void, DTOYotiClientKeyResponse, void>({
    path: AgeVerificationApiList.YotiClientKey,
    options: {
      staleTime: Infinity,
      gcTime: Infinity,
    },
  });
};

export const useYotiLivenessCheckMutation = (
  options: RestMutationOptions<DTOYotiLivenessCheckRequest, DTOYotiLivenessCheckResponse> = {}
) => {
  return useRestMutation<DTOYotiLivenessCheckRequest, DTOYotiLivenessCheckResponse>({
    path: AgeVerificationApiList.YotiLivenessCheck,
    method: 'post',
    options,
  });
};

export const useYotiLivenessCheckV2Mutation = (
  options: RestMutationOptions<DTOYotiLivenessCheckV2Request, DTOYotiLivenessCheckResponse> = {}
) => {
  return useRestMutation<DTOYotiLivenessCheckV2Request, DTOYotiLivenessCheckResponse>({
    path: AgeVerificationApiList.YotiLivenessCheckV2,
    method: 'post',
    options,
  });
};

export const useYotiLivenessGuideQuery = () => {
  return useRestQuery<void, DTOYotiLivenessGuideResponse, void>({
    path: AgeVerificationApiList.YotiLivenessGuide,
  });
};

export const useYotiAvsHeadlessGuideQuery = () => {
  return useRestQuery<void, DTOYotiAvsHeadlessGuideResponse, void>({
    path: AgeVerificationApiList.YotiAvsHeadlessGuide,
  });
};

export const useYotiAvsHeadlessSessionMutation = (
  options: RestMutationOptions<
    DTOYotiAvsHeadlessSessionRequest,
    DTOYotiAvsHeadlessSessionResponse,
    ErrorResponse
  > = {}
) => {
  return useRestMutation<
    DTOYotiAvsHeadlessSessionRequest,
    DTOYotiAvsHeadlessSessionResponse,
    ErrorResponse
  >({
    path: AgeVerificationApiList.YotiAvsHeadlessSession,
    method: 'post',
    options,
  });
};

export const useYotiLivenessCheckResultPolling = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearPolling = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  useEffect(() => clearPolling, []);

  const poll = useCallback((requestId: string) => {
    clearPolling();

    return new Promise<DTOYotiLivenessCheckResultResponse>((resolve, reject) => {
      const executePolling = async () => {
        try {
          const { data } = await client.get<SuccessResponse<DTOYotiLivenessCheckResultResponse>>(
            AgeVerificationApiList.YotiLivenessCheckResult,
            { params: { requestId } }
          );

          switch (data.result.processingStatus) {
            case 'PENDING':
              timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
              break;
            case 'COMPLETED':
            case 'FAILED':
              resolve(data.result);
              break;
          }
        } catch (error) {
          reject(error);
        }
      };

      timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
    });
  }, []);

  return { poll };
};

export const useYotiIdvResultPolling = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearPolling = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  useEffect(() => clearPolling, []);

  const poll = useCallback((requestId: string) => {
    clearPolling();

    return new Promise<DTOYotiIdvResultResponse>((resolve, reject) => {
      let createdAttempts = 0;

      const executePolling = async () => {
        try {
          const { data } = await client.get<SuccessResponse<DTOYotiIdvResultResponse>>(
            AgeVerificationApiList.YotiLivenessCheckResult,
            { params: { requestId } }
          );

          switch (data.result.processingStatus) {
            // CREATED: 세션 생성·미제출(또는 제출 직후 아직 PENDING 전). verifying 중에는
            // 폴링을 유지하되, 고착 방지를 위해 상한을 둔다.
            // @see https://hpcnt.slack.com/archives/C0APJSAHTS9/p1786455294098229?thread_ts=1786442766.744569&cid=C0APJSAHTS9
            case 'CREATED':
              createdAttempts += 1;
              if (createdAttempts >= MAX_IDV_CREATED_POLLING_ATTEMPTS) {
                clearPolling();
                reject(new Error('Yoti IDV result polling timed out'));
                return;
              }
              timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
              break;
            case 'PENDING':
              timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
              break;
            case 'COMPLETED':
            case 'FAILED':
              resolve(data.result);
              break;
            default:
              reject(
                new Error(`Unknown Yoti IDV processingStatus: ${JSON.stringify(data.result)}`)
              );
          }
        } catch (error) {
          reject(error);
        }
      };

      timeoutRef.current = setTimeout(executePolling, POLLING_INTERVAL_MS);
    });
  }, []);

  return { poll };
};
